

























































import { Component, Prop, Vue } from 'vue-property-decorator'

/**
 * Hero header of the landing page for St. Vincenz Kliniken (Vue 2 + TypeScript).
 * Layout: logo top left, title and tagline below, line drawing of the clinic at the bottom.
 * The logo and the drawing are customer specific (logo in /public, strokes generated by
 * server/Documentation/new-customer/vectorize-line-art.js from l3.vincenz/vincenz_clinic.webp).
 *
 * Animation: on load several pens draw the lines of the clinic at the same time while the tagline fades in.
 * After this intro random windows of the clinic light up (flicker) one after another.
 */
import { CLINIC_W, CLINIC_H, CLINIC_PATHS } from './vincenzClinicPaths'

/** Number of pens that draw at the same time (each pen owns a vertical band of the drawing) */
const PENS = 8
/** Time the busiest pen needs to draw all its strokes, in ms */
const DRAW_MS = 3800
const START_MS = 200
const MIN_STROKE_MS = 12
const STROKE_GAP_MS = 4

interface Stroke { d: string, style: { animationDelay: string, animationDuration: string } }

/** Distributes the strokes over the pens (by x position, balanced by length) and calculates delay and duration */
function planStrokes (): { strokes: Stroke[], endMs: number } {
  const items = CLINIC_PATHS.map(p => ({ len: p[0], d: p[1], cx: p[2] })).sort((a, b) => a.cx - b.cx)
  const total = items.reduce((sum, p) => sum + p.len, 0)
  const pens: typeof items[] = Array.from({ length: PENS }, () => [])
  let acc = 0
  for (const item of items) {
    pens[Math.min(PENS - 1, Math.floor(((acc + item.len / 2) / total) * PENS))].push(item)
    acc += item.len
  }
  // Pixels per ms so that the busiest pen finishes after DRAW_MS
  const speed = Math.max(...pens.map(pen => pen.reduce((sum, p) => sum + p.len, 0) /
    Math.max(1, DRAW_MS - pen.length * STROKE_GAP_MS)))
  const strokes: Stroke[] = []
  let endMs = 0
  for (const pen of pens) {
    let t = START_MS
    for (const item of pen) {
      const dur = Math.max(MIN_STROKE_MS, item.len / speed)
      strokes.push({
        d: item.d,
        style: { animationDelay: Math.round(t) + 'ms', animationDuration: Math.round(dur) + 'ms' }
      })
      t += dur + STROKE_GAP_MS
    }
    endMs = Math.max(endMs, t)
  }
  return { strokes, endMs }
}

const PLAN = planStrokes()

/** Duration of the intro in ms (windows start to flicker afterwards) */
const INTRO_MS = Math.round(PLAN.endMs) + 300

/** Windows of the drawing as [x, y, width, height] in image pixels (detected from the drawing) */
const WINDOWS: number[][] = [
  [98,117,9,7], [101,130,6,10], [104,125,3,4], [120,126,6,4], [131,110,11,7], [132,125,10,6], [146,110,5,7], [147,125,4,6],
  [155,111,11,6], [157,126,6,5], [171,112,13,7], [171,127,2,4], [177,127,5,5], [187,128,4,4], [187,141,5,5], [187,155,14,7],
  [188,113,14,6], [196,129,4,4], [196,141,4,5], [206,114,14,6], [209,155,9,7], [211,141,11,5], [212,128,10,5], [223,144,5,3],
  [229,156,2,5], [233,154,5,5], [238,117,12,13], [243,131,2,7], [274,99,19,20], [323,113,5,12], [324,140,6,10], [330,110,5,13],
  [331,138,6,11], [337,107,6,13], [338,136,5,13], [351,86,11,12], [351,104,12,11], [352,113,11,11], [352,122,11,11], [352,132,12,23],
  [398,96,20,15], [399,108,19,14], [399,121,19,10], [399,131,19,9], [399,140,19,10], [399,150,19,9], [463,106,3,18], [492,70,6,6],
  [506,126,6,3], [518,124,4,5], [530,134,4,7], [542,121,4,7], [544,106,2,6], [544,135,3,6], [551,106,3,6], [552,122,3,6],
  [552,135,3,5], [560,146,3,9], [564,146,4,8], [569,145,7,10], [578,145,7,10], [586,144,9,11], [596,138,20,5], [596,143,21,12],
  [611,64,3,7], [611,80,3,6], [611,97,4,5], [612,112,3,7], [624,93,21,15], [624,113,21,12], [624,123,21,9], [624,131,21,10],
  [624,142,8,11], [634,141,11,11], [657,57,6,10], [658,78,6,9], [658,99,6,8], [658,120,6,8], [658,143,8,8], [726,40,7,17],
  [726,59,7,17], [726,79,7,17], [726,99,7,16], [726,119,7,18], [727,140,6,10], [736,122,3,15], [736,140,3,9], [743,125,6,23],
  [753,132,2,3], [756,132,2,14], [760,131,11,4], [765,121,4,3], [772,131,2,14], [794,112,6,12], [806,107,8,7], [806,121,9,3],
  [820,108,9,6], [820,121,8,3], [830,141,18,16], [835,108,7,6], [835,121,7,6], [848,109,6,6], [848,121,7,6], [856,147,4,11],
  [861,109,7,6], [861,122,8,6], [861,147,7,11], [870,146,5,12], [873,109,7,6], [873,122,7,6], [883,146,6,6], [885,109,7,6],
  [885,122,7,6], [897,110,6,6], [897,122,6,6], [897,146,4,6], [908,111,7,5], [908,122,7,5], [910,147,6,5], [919,111,5,5],
  [955,100,2,5], [955,111,2,5], [955,123,3,5], [955,141,7,14], [961,97,2,6], [961,109,2,6], [961,121,2,6], [963,141,5,13],
  [967,95,3,6], [967,107,2,6], [967,120,3,5], [974,92,3,6], [974,106,2,5], [974,118,3,6], [974,131,3,3], [981,89,3,7],
  [981,103,3,6], [981,117,4,6], [981,130,4,6], [982,144,3,6], [989,86,3,6], [989,100,4,7], [989,115,4,6], [989,129,4,6],
  [990,144,3,5], [998,97,4,8], [998,113,4,6], [998,128,4,7], [998,143,5,6], [999,82,3,7], [1008,94,4,7], [1008,110,5,8],
  [1008,126,5,7], [1008,143,6,7], [1009,78,4,7], [1019,91,5,7], [1019,107,5,9], [1019,124,6,8], [1019,142,5,7], [1020,73,4,7],
  [1029,97,8,7], [1030,88,5,9], [1031,105,5,8], [1031,123,4,8], [1031,141,5,9], [1033,68,3,10], [1056,148,22,7], [1079,89,21,12],
  [1079,101,8,8], [1079,124,8,8], [1080,55,7,9], [1080,78,7,9], [1088,78,7,8], [1089,56,5,6], [1089,101,6,7], [1089,124,5,8],
  [1095,50,9,6], [1095,57,9,7], [1096,72,9,6], [1096,79,9,8], [1096,94,9,6], [1096,101,9,8], [1096,116,8,7], [1096,124,8,8],
  [1105,58,3,6], [1105,124,4,8], [1106,80,2,7], [1106,102,2,7], [1109,52,8,6], [1109,58,8,8], [1109,103,10,7], [1110,74,8,6],
  [1110,80,8,8], [1110,95,8,7], [1110,117,8,6], [1110,125,8,7], [1119,60,8,7], [1119,125,8,7], [1120,81,7,8], [1120,103,7,7]
]

@Component
export default class L3VincenzHeroHeader extends Vue {
  @Prop({ type: String, default: 'Vincenz Lernwelt' }) readonly title!: string
  @Prop({ type: String, default: 'Wissen, das wirkt.' }) readonly taglineHead!: string
  @Prop({ type: String, default: 'Schulungen und Fachwissen - jederzeit, überall.' }) readonly taglineText!: string
  /** White version of the customer logo (mark left, "St. Vincenz" and "Kliniken" in two lines on the right) */
  @Prop({ type: String, default: '' }) readonly logoSrc!: string
  @Prop({ type: String, default: 'St. Vincenz Kliniken' }) readonly logoAlt!: string
  @Prop({ type: String, default: 'Strichzeichnung der Vincenz Klinik Paderborn' }) readonly clinicAlt!: string
  /** Switch the flickering windows on/off */
  @Prop({ type: Boolean, default: true }) readonly flicker!: boolean

  readonly CLINIC_W = CLINIC_W
  readonly CLINIC_H = CLINIC_H

  // Non-reactive runtime variables for the window animation
  private timer = 0
  private visible = true
  private io: IntersectionObserver | null = null

  get windows (): number[][] {
    return WINDOWS
  }

  get strokes (): Stroke[] {
    return PLAN.strokes
  }

  get logoUrl (): string {
    return this.logoSrc || '/VincenzLogoWhite.svg'
  }

  mounted (): void {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !this.flicker) return
    if ('IntersectionObserver' in window) {
      // Only flicker while the header is visible
      this.io = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting })
      this.io.observe(this.$el)
    }
    this.timer = window.setTimeout(this.lightRandomWindow, INTRO_MS)
  }

  beforeDestroy (): void {
    window.clearTimeout(this.timer)
    if (this.io) this.io.disconnect()
  }

  /** Lights up a random window and schedules the next one */
  lightRandomWindow (): void {
    if (this.visible && !document.hidden) {
      // Sometimes two windows at once
      const count = Math.random() < 0.25 ? 2 : 1
      for (let n = 0; n < count; n++) {
        const refs = this.$refs['win' + Math.floor(Math.random() * WINDOWS.length)] as SVGRectElement[] | undefined
        const el = refs && refs[0]
        if (!el || el.classList.contains('vz-hero__window--on')) continue
        el.style.animationDuration = (1.1 + Math.random() * 1.4).toFixed(2) + 's'
        el.classList.add('vz-hero__window--on')
        el.addEventListener('animationend', () => el.classList.remove('vz-hero__window--on'), { once: true })
      }
    }
    this.timer = window.setTimeout(this.lightRandomWindow, 250 + Math.random() * 900)
  }
}
